/**
 * Step 3: 전처리 설정
 *
 * - 파일 소스: AWS RAG 방식의 "파서 선택" UI.
 *   2단계에서 업로드한 파일 확장자에 따라 노출할 파서를 동적으로 결정한다.
 *   (TXT/Excel → 기본 파서만, PDF → 기본+BDA+FM, 이미지 → BDA+FM)
 *   미구현 파서(BDA/FM)는 "준비 중"으로 표시하고 선택 불가.
 * - 웹/DB 소스: 기존 체크박스 방식 유지.
 *
 * 복합 소스일 때 SourceTabs로 탭 전환하여 소스별 옵션을 설정한다.
 */

import { useEffect } from 'react';
import { PreprocessingConfig } from '../../../api';
import {
  SourceType,
  ParserConfig,
  ParserType,
  PARSER_OPTIONS,
  FM_PARSER_MODELS,
  getAvailableParsers,
} from '../shared/pipeline.types';
import { SourceTabs } from '../shared/SourceTabs';
import { Badge, Checkbox, Select } from '../../ui';
import preprocessColumnIllust from '../../../assets/illustrations/preprocessing-column-structure.svg';
import preprocessWhitespaceIllust from '../../../assets/illustrations/preprocessing-whitespace.svg';
import preprocessNullIllust from '../../../assets/illustrations/preprocessing-null.svg';
import preprocessDatetimeIllust from '../../../assets/illustrations/preprocessing-datetime.svg';
import preprocessNumberIllust from '../../../assets/illustrations/preprocessing-number.svg';
import preprocessDedupeIllust from '../../../assets/illustrations/preprocessing-dedupe.svg';
import preprocessTextColumnsIllust from '../../../assets/illustrations/preprocessing-text-columns.svg';
import preprocessMaskingIllust from '../../../assets/illustrations/preprocessing-masking.svg';
import preprocessBoilerplateIllust from '../../../assets/illustrations/preprocessing-boilerplate.svg';
import preprocessMainContentIllust from '../../../assets/illustrations/preprocessing-main-content.svg';
import preprocessAdsIllust from '../../../assets/illustrations/preprocessing-ads-trackers.svg';
import { isAdminRole } from '../../../utils/role';
import { authApi } from '../../../api';
// 사용자 화면용 전처리 그림 (사용자 테마 색)
import userPreprocessBoilerplateIllust from '../../../assets/illustrations/user-preprocessing-boilerplate.svg';
import userPreprocessMainContentIllust from '../../../assets/illustrations/user-preprocessing-main-content.svg';
import userPreprocessAdsIllust from '../../../assets/illustrations/user-preprocessing-ads-trackers.svg';
import userPreprocessWhitespaceIllust from '../../../assets/illustrations/user-preprocessing-whitespace.svg';

// 웹 · DB 전처리 옵션 카드 그림 (없는 옵션은 그림 없이 표시)
const PREPROCESS_ILLUSTS: Partial<Record<keyof PreprocessingConfig, string>> = {
  // 웹
  removeBoilerplate: preprocessBoilerplateIllust,
  mainContentOnly:   preprocessMainContentIllust,
  removeAds:         preprocessAdsIllust,
  // 웹 · DB 공통
  cleanWhitespace:   preprocessWhitespaceIllust,
  // DB
  extractTables:     preprocessColumnIllust,
  dbNullHandling:    preprocessNullIllust,
  dbDateFormat:      preprocessDatetimeIllust,
  dbNumberFormat:    preprocessNumberIllust,
  dbDeduplication:   preprocessDedupeIllust,
  dbTextColumnsOnly: preprocessTextColumnsIllust,
  dbMaskSensitive:   preprocessMaskingIllust,
};

// 사용자(로그인 역할 기준)일 때 그림 - 없는 옵션은 위 기본(관리자) 그림
const USER_PREPROCESS_ILLUSTS: Partial<Record<keyof PreprocessingConfig, string>> = {
  removeBoilerplate: userPreprocessBoilerplateIllust,
  mainContentOnly:   userPreprocessMainContentIllust,
  removeAds:         userPreprocessAdsIllust,
  cleanWhitespace:   userPreprocessWhitespaceIllust,
};

// 파서 카드 제목 옆 강조 배지
const PARSER_HIGHLIGHT: Partial<Record<ParserType, string>> = {
  'foundation-model': '한국어 추천',
};

// 웹/DB 소스용 전처리 옵션 정의 (기존 체크박스 방식 유지)
const PREPROCESS_CONFIGS: Record<
  'web' | 'database',
  {
    desc: string;
    options: Array<{ key: keyof PreprocessingConfig; label: string; desc: string }>;
  }
> = {
  web: {
    desc: '',
    options: [
      { key: 'removeBoilerplate', label: '보일러플레이트 제거', desc: '네비게이션·헤더·푸터 등 반복 요소 제거 (본문이 사라지면 자동 복원)' },
      { key: 'mainContentOnly',   label: '본문 우선 추출',      desc: 'main·article 등 주요 콘텐츠 영역을 우선 추출' },
      { key: 'cleanWhitespace',   label: '공백 정리',            desc: '불필요한 공백·빈 줄 정리' },
      { key: 'removeAds',         label: '광고/추적 요소 제거',  desc: '광고·스폰서·추적 영역 자동 감지 및 제거' },
    ],
  },
  database: {
    desc: '',
    options: [
      { key: 'extractTables',      label: '컬럼 구조화',          desc: '컬럼명을 포함한 구조화된 텍스트 생성' },
      { key: 'cleanWhitespace',    label: '공백 정리',             desc: '불필요한 공백·줄바꿈 정리' },
      { key: 'dbNullHandling',     label: 'NULL 값 처리',          desc: 'NULL 컬럼을 텍스트에서 제외하거나 "없음"으로 표시' },
      { key: 'dbDateFormat',       label: '날짜/시간 정규화',      desc: '날짜·시간 값을 읽기 쉬운 한국어 형식으로 변환' },
      { key: 'dbNumberFormat',     label: '숫자 포맷 정규화',      desc: '천단위 구분(1,000,000) 및 단위 추가 (원, 개 등)' },
      { key: 'dbDeduplication',    label: '중복 레코드 제거',      desc: '동일한 내용의 중복 행을 하나만 포함' },
      { key: 'dbTextColumnsOnly',  label: '텍스트 컬럼만 추출',    desc: '코드·ID 등 짧은 컬럼 제외, 실제 내용 컬럼만 KB에 포함' },
      { key: 'dbMaskSensitive',    label: '민감 정보 마스킹',      desc: '이메일·전화번호·주민번호 패턴 자동 감지 후 마스킹' },
    ],
  },
};

interface PreprocessingStepProps {
  activeSources: SourceType[];
  activeTab: SourceType;
  onActiveTabChange: (tab: SourceType) => void;

  // 파일 소스 — 파서 선택 방식
  files: File[];
  driveFiles?: { fileId: string; fileName: string }[];
  dropboxFiles?: { filePath: string; fileName: string }[];
  fileParser: ParserConfig;
  onFileParserChange: (cfg: ParserConfig) => void;

  // 웹/DB 소스 — 기존 체크박스 방식
  webPreprocessing: PreprocessingConfig;
  onWebPreprocessingChange: (cfg: PreprocessingConfig) => void;
  dbPreprocessing: PreprocessingConfig;
  onDbPreprocessingChange: (cfg: PreprocessingConfig) => void;
}

export function PreprocessingStep({
  activeSources,
  activeTab,
  onActiveTabChange,
  files,
  driveFiles = [],
  dropboxFiles = [],
  fileParser,
  onFileParserChange,
  webPreprocessing,
  onWebPreprocessingChange,
  dbPreprocessing,
  onDbPreprocessingChange,
}: PreprocessingStepProps) {
  return (
    <div className="form-item--md">
      <div className="form-label">
        전처리 설정
        <div className="optional">
          {activeTab === 'file'
            ? '문서를 파싱할 방식을 선택하세요. 업로드한 파일 형식에 맞는 파서만 표시됩니다.'
            : '텍스트를 추출할 때 적용할 옵션을 선택하세요.'}
        </div>
      </div>

      {/* 복합 소스 탭 — 단일 소스면 SourceTabs 내부에서 null 반환 */}
      <SourceTabs sources={activeSources} active={activeTab} onChange={onActiveTabChange} />

      {/* 파일 소스: 파서 선택 UI */}
      {activeTab === 'file' && (
        <FileParserSelect files={files} driveFiles={driveFiles} dropboxFiles={dropboxFiles} value={fileParser} onChange={onFileParserChange} />
      )}

      {/* 웹/DB 소스: 기존 체크박스 UI */}
      {(activeTab === 'web' || activeTab === 'database') && (
        <CheckboxPreprocess
          source={activeTab}
          state={activeTab === 'web' ? webPreprocessing : dbPreprocessing}
          setter={activeTab === 'web' ? onWebPreprocessingChange : onDbPreprocessingChange}
        />
      )}
    </div>
  );
}

// ── 파일 소스: 파서 선택 카드 ──
function FileParserSelect({
  files,
  driveFiles = [],
  dropboxFiles = [],
  value,
  onChange,
}: {
  files: File[];
  driveFiles?: { fileId: string; fileName: string }[];
  dropboxFiles?: { filePath: string; fileName: string }[];
  value: ParserConfig;
  onChange: (cfg: ParserConfig) => void;
}) {
  const allFiles = [
    ...files,
    ...driveFiles.map(df => ({ name: df.fileName } as File)),
    ...dropboxFiles.map(df => ({ name: df.fileName } as File)),
  ];
  const availableParsers = getAvailableParsers(allFiles);

  // 현재 선택된 파서가 노출 목록에 없으면 첫 번째 구현된 파서로 보정
  const selected = availableParsers.includes(value.parser)
    ? value.parser
    : availableParsers.find((p) => PARSER_OPTIONS[p].implemented) ?? availableParsers[0];

  // 화면상 보정된 파서(selected)가 실제 저장값(value.parser)과 다르면 저장값도 동기화한다.
  // (예: 이미지만 업로드 시 사용 가능한 파서가 FM뿐이라 자동으로 FM이 선택된 것처럼
  //  보이지만, 사용자가 카드를 직접 클릭하지 않으면 저장값이 'default'로 남아
  //  백엔드가 기본 파서로 처리하는 버그를 방지)
  useEffect(() => {
    if (selected && selected !== value.parser) {
      onChange({ ...value, parser: selected });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // 화면 표시 순서만 AI 파서 먼저 (자동 선택 기준인 availableParsers 순서는 그대로)
  const displayParsers = [...availableParsers].sort(
    (a, b) => (a === 'foundation-model' ? -1 : 0) - (b === 'foundation-model' ? -1 : 0),
  );

  // 모델 특징 표시용 - 선택된 모델 (없으면 첫 번째)
  const selectedModel = FM_PARSER_MODELS.find((m) => m.id === value.fmModelId) ?? FM_PARSER_MODELS[0];

  const handleSelect = (parser: ParserType) => {
    if (!PARSER_OPTIONS[parser].implemented) return; // 미구현 파서는 선택 불가
    onChange({ ...value, parser });
  };

  return (
    <div className="pipe-tab-content">
      {files.length === 0 && driveFiles.length === 0 && dropboxFiles.length === 0 && (
        <div className="preprocess-warning-text pipe-section-desc">
          아직 업로드된 파일이 없어 기본 파서만 표시됩니다.
        </div>
      )}

      {/* 파서 카드 — 공통 라디오 선택 카드(.choice-card, index.css) 세로 목록
          선택된 AI 파서는 같은 테두리 안에 설정 펼침 (.pipe-parser-card 가 바깥 틀) */}
      <div className="choice-cards choice-cards--single" role="radiogroup" aria-label="파서">
        {displayParsers.map((p) => {
          const meta = PARSER_OPTIONS[p];
          const isSelected = selected === p;
          const disabled = !meta.implemented;
          return (
            <div key={p} className={`pipe-parser-card${isSelected ? ' selected' : ''}`}>
              <button
                type="button"
                className={`choice-card${isSelected ? ' active' : ''}`}
                role="radio"
                aria-checked={isSelected}
                disabled={disabled}
                onClick={() => handleSelect(p)}
              >
                <span className="choice-card-radio" />
                <span className="choice-card-body">
                  <span className="choice-card-title">
                    {meta.title}
                    {PARSER_HIGHLIGHT[p] && <Badge color="secondary">{PARSER_HIGHLIGHT[p]}</Badge>}
                    {disabled && <Badge color="medium">준비 중</Badge>}
                  </span>
                  <span className="choice-card-desc">{meta.desc}</span>
                  <span className="choice-card-tags">
                    <Badge variant="type">{meta.supportedFiles}</Badge>
                    <Badge variant="type">{meta.pricing}</Badge>
                  </span>
                </span>
              </button>

              {/* AI 파서(FM) 선택 시: 모델 선택 + 커스텀 프롬프트 */}
              {isSelected && p === 'foundation-model' && (
                <div className="pipe-parser-fm-options">
                  <div className="pipe-parser-fm-row">
                    <div className="pipe-settings-field">
                      <label className="pipe-settings-label">파싱 모델</label>
                      <Select
                        value={value.fmModelId ?? FM_PARSER_MODELS[0].id}
                        onChange={(e) => onChange({ ...value, fmModelId: e.target.value })}
                        options={FM_PARSER_MODELS.map((m) => ({ value: m.id, label: m.name }))}
                      />
                    </div>
                    <div className="pipe-settings-field">
                      <label className="pipe-settings-label">모델 특징</label>
                      <div className="pipe-parser-model-tags">
                        {selectedModel.note.split('·').map((t) => (
                          <Badge key={t} variant="type">{t.trim()}</Badge>
                        ))}
                        <Badge variant="type">입력 ${selectedModel.inputCost} · 출력 ${selectedModel.outputCost} / 100만 토큰</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="pipe-settings-field">
                    <label className="pipe-settings-label">
                      커스텀 파싱 프롬프트 <span className="optional">(선택)</span>
                    </label>
                    <textarea
                      className="pipe-settings-input"
                      rows={5}
                      placeholder="예: 문서의 표는 마크다운 표로 변환하고, 이미지 캡션도 텍스트로 포함해줘."
                      value={value.parsingPrompt ?? ''}
                      onChange={(e) => onChange({ ...value, parsingPrompt: e.target.value })}
                      style={{ resize: 'vertical' }}
                    />
                    <div className="pipe-parser-hint">비워두면 기본 설정으로 파싱합니다.</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 웹/DB 소스: 기존 체크박스 ──
function CheckboxPreprocess({
  source,
  state,
  setter,
}: {
  source: 'web' | 'database';
  state: PreprocessingConfig;
  setter: (cfg: PreprocessingConfig) => void;
}) {
  const cfg = PREPROCESS_CONFIGS[source];
  // 사용자면 사용자 테마 그림 우선 (소스 선택 · 청킹 단계와 같은 로그인 역할 기준)
  const isAdmin = isAdminRole(authApi.getUser()?.role);
  return (
    <div className="pipe-tab-content">
      <div className="pipe-section-desc">{cfg.desc}</div>
      {/* 소스 선택 카드와 같은 체크박스 카드 (체크 · 그림 · 이름 · 설명) */}
      <div className="pipe-source-select-cards pipe-preprocess-cards">
        {cfg.options.map(({ key, label, desc }) => {
          const checked = state[key] as boolean;
          const illust = (!isAdmin && USER_PREPROCESS_ILLUSTS[key]) || PREPROCESS_ILLUSTS[key];
          return (
            <label key={key} className={`pipe-source-select-card${checked ? ' active' : ''}`}>
              <Checkbox
                checked={checked}
                onChange={(e) => setter({ ...state, [key]: e.target.checked })}
              />
              {illust && <img className="pipeline-step-illust" src={illust} alt="" />}
              <div className="pipe-source-select-content">
                <div className="pipe-source-select-label">{label}</div>
                <div className="pipe-source-select-sub">{desc}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
